import React, { useEffect, useState, useCallback } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import { FaCalendarPlus, FaUserShield, FaUsers, FaClipboardList, FaSave, FaArrowRight, FaStar } from "react-icons/fa";

// دالة بسيطة للـ Debounce للتحكم في عدد طلبات البحث
const debounce = (func, delay) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
};

const PatrolRosterCreatePage = () => {
    const navigate = useNavigate();
    const { backEnd_request } = BackMethods();

    // دالة للحصول على تاريخ اليوم بصيغة YYYY-MM-DD
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [users, setUsers] = useState([]);
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [loadingStaff, setLoadingStaff] = useState(false); // حالة تحميل الموظفين
    const [successMessage, setSuccessMessage] = useState("");

    const [userId, setUserId] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [patrolDate, setPatrolDate] = useState(getTodayDate());
    const [leaderStaffId, setLeaderStaffId] = useState(null);
    const [memberStaffIds, setMemberStaffIds] = useState([]);
    const [notes, setNotes] = useState("");

    // دالة جلب المستخدمين المسؤولين
    const fetchUsers = async (search = "") => {
        setSearching(true);
        try {
            const res = await backEnd_request.get("/admin/UsersManagement", {
                params: {
                    SearchTerm: search,
                    PageSize: 100,
                    Page: 1
                }
            });
            const userData = res.data.users || [];
            setUsers(userData.map((u) => ({
                value: u.userId,
                label: u.username
            })));
        } catch (error) {
            console.error("Error loading users:", error);
        } finally {
            setSearching(false);
        }
    };

    // دالة جلب الموظفين المتاحين بناءً على التاريخ المختار
    const fetchStaff = async (date) => {
        if (!date) return;
        setLoadingStaff(true);
        try {
            // تمرير التاريخ كـ Query Parameter للسيرفر
            const res = await backEnd_request.get("/PatrolRosters/available-staff", {
                params: { date: date }
            });

            setStaff(res.data.map((s) => ({
                value: s.patrolStaffId,
                label: `${s.firstName} ${s.lastName} (${s.governorate})`
            })));

            // إعادة تعيين الموظفين المختارين عند تغيير التاريخ لتجنب اختيار موظف غير متاح في اليوم الجديد
            setLeaderStaffId(null);
            setMemberStaffIds([]);
        } catch (error) {
            console.error("Error loading staff:", error);
        } finally {
            setLoadingStaff(false);
        }
    };

    const debouncedFetchUsers = useCallback(
        debounce((inputValue) => fetchUsers(inputValue), 500),
        []
    );

    // تحديث قائمة الموظفين عند تحميل الصفحة أول مرة
    useEffect(() => {
        fetchUsers("");
    }, []);

    // تحديث قائمة الموظفين المتاحين كلما تغير التاريخ في حقل التاريخ
    useEffect(() => {
        fetchStaff(patrolDate);
    }, [patrolDate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userId) return alert("الرجاء اختيار المستخدم المسؤول");
        if (!leaderStaffId) return alert("يجب اختيار رئيس الدورية!");

        setLoading(true);
        const payload = {
            userId,
            patrolDate,
            leaderStaffId,
            memberStaffIds: memberStaffIds.map((s) => s.value),
            notes,
        };

        try {
            await backEnd_request.post("/PatrolRosters", payload);
            setSuccessMessage("تم حفظ الدورية بنجاح ✅");
            setUserId(null);
            setSelectedUser(null);
            setLeaderStaffId(null);
            setMemberStaffIds([]);
            setNotes("");
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Error saving roster:", error);
            alert("حدث خطأ أثناء حفظ الدورية");
        } finally {
            setLoading(false);
        }
    };

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            padding: '2px',
            borderRadius: '8px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: '#0d6efd' }
        })
    };

    return (
        <div className="container py-5" dir="rtl">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold text-dark mb-1">
                        <FaCalendarPlus className="ms-2 text-primary" /> إنشاء دورية جديدة
                    </h2>
                    <p className="text-muted small">قم بتعبئة البيانات لتخصيص طاقم الدورية اليومي</p>
                </div>
                <button className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={() => navigate(-1)}>
                    <FaArrowRight className="ms-1" /> رجوع
                </button>
            </div>

            {successMessage && (
                <div className="alert alert-success border-0 shadow-sm mb-4">
                    {successMessage}
                </div>
            )}

            <div className="card border-0 shadow-sm rounded-4">
                <div className="card-body p-4 p-md-5">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-4">
                            <div className="col-md-6">
                                <label className="form-label fw-bold"><FaUserShield className="ms-1 text-primary" /> المستخدم المسؤول (Admin)</label>
                                <Select
                                    options={
                                        selectedUser && !users.some((u) => u.value === selectedUser.value)
                                            ? [selectedUser, ...users]
                                            : users
                                    }
                                    styles={customSelectStyles}
                                    placeholder="ابحث عن اسم المستخدم..."
                                    isLoading={searching}
                                    onInputChange={(newValue, { action }) => {
                                        if (action === "input-change") {
                                            debouncedFetchUsers(newValue);
                                        }
                                    }}
                                    value={selectedUser}
                                    onChange={(option) => {
                                        setSelectedUser(option);
                                        setUserId(option ? option.value : null);
                                    }}
                                    isSearchable
                                    isClearable
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-bold"><FaClipboardList className="ms-1 text-primary" /> تاريخ الدورية</label>
                                <input
                                    type="date"
                                    className="form-control form-control-lg fs-6 rounded-3"
                                    value={patrolDate}
                                    onChange={(e) => setPatrolDate(e.target.value)}
                                    required
                                />
                                <small className="text-muted mt-1 d-block">سيتم تحديث قائمة الموظفين المتاحين بناءً على هذا التاريخ.</small>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-bold text-warning"><FaStar className="ms-1" /> رئيس الدورية (القائد)</label>
                                <Select
                                    options={staff}
                                    styles={customSelectStyles}
                                    placeholder={loadingStaff ? "جاري التحميل..." : "اختر قائد الدورية..."}
                                    isLoading={loadingStaff}
                                    value={staff.find(s => s.value === leaderStaffId) || null}
                                    onChange={(e) => setLeaderStaffId(e ? e.value : null)}
                                    isSearchable
                                    isClearable
                                    noOptionsMessage={() => "لا يوجد موظفون متاحون لهذا التاريخ"}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-bold"><FaUsers className="ms-1 text-primary" /> أعضاء الدورية</label>
                                <Select
                                    options={staff}
                                    styles={customSelectStyles}
                                    placeholder={loadingStaff ? "جاري التحميل..." : "اختر الأعضاء المرافقين..."}
                                    isLoading={loadingStaff}
                                    value={memberStaffIds}
                                    onChange={(selected) => setMemberStaffIds(selected || [])}
                                    isMulti
                                    isSearchable
                                    noOptionsMessage={() => "لا يوجد موظفون متاحون لهذا التاريخ"}
                                />
                            </div>

                            <div className="col-12">
                                <label className="form-label fw-bold">ملاحظات إضافية</label>
                                <textarea
                                    className="form-control rounded-3"
                                    rows={4}
                                    placeholder="اكتب أي تعليمات أو ملاحظات خاصة بالدورية هنا..."
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                ></textarea>
                            </div>

                            <div className="col-12 mt-5">
                                <div className="d-grid d-md-flex justify-content-md-end gap-3">
                                    <button
                                        type="button"
                                        className="btn btn-light btn-lg px-5 rounded-3 fw-bold"
                                        onClick={() => navigate("/admin/patrol-rosters")}
                                    >
                                        إلغاء
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-lg px-5 rounded-3 fw-bold shadow"
                                        disabled={loading || loadingStaff}
                                    >
                                        {loading ? (
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                        ) : (
                                            <FaSave className="ms-2" />
                                        )}
                                        حفظ وإرسال
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PatrolRosterCreatePage;